import { resolveOrganisationDisplayName } from "../../utils/string";
import { decodeContactInfo, encodeContactInfo } from "../contactInfo";
import { decodeEmissionFactor, encodeEmissionFactor } from "../emissionFactor";
import { decodeProjectEnvironmentalCommitment, encodeProjectEnvironmentalCommitment } from "../environmentalCommitment";
import { decodeProjectEnvironmentalTarget, encodeProjectEnvironmentalTarget } from "../environmentalTarget";
import { decodeFinancial, encodeFinancial } from "../financial";
import { decodeCollaboratorDetails, encodeCollaboratorDetail } from "../organisation";
import { decodeProjectFramework, encodeProjectFramework } from "../projectFramework";
import { decodeProjectLabourHourCommitment, encodeProjectLabourHourCommitment } from "../projectLabourHourCommitment";
import { decodeProjectLocalContent, encodeProjectLocalContent } from "../projectLocalContent";
import { decodeProjectPolicy, encodeProjectPolicy } from "../projectPolicy";
import { decodeProjectSocialSpendCommitment, encodeProjectSocialSpendCommitment } from "../projectSocialSpendCommitment";
export const decodeProject = async (project, getImageUrl) => {
    const presignedUrl = await getImageUrl(project.organisation_logo);
    return {
        archived: project.archived,
        closedDate: project.closed_date,
        collaborators: project.collaborators || [],
        commitmentsEnvironmental: (project.commitments_environmental || []).map((c) => decodeProjectEnvironmentalCommitment(c)),
        commitmentsHours: project.commitments_hours.map((c) => decodeProjectLabourHourCommitment(c)),
        commitmentsSpend: project.commitments_spend.map((c) => decodeProjectSocialSpendCommitment(c)),
        created: new Date(project.created),
        emissionFactors: (project.emission_factors || []).map(decodeEmissionFactor),
        estimatedProgress: project.estimated_progress,
        financial: decodeFinancial(project.financial),
        hammertechProjectId: project.hammertech_project_id,
        id: project.project_id,
        isClosed: project.is_closed ?? false,
        isShared: project.is_shared ?? false,
        localContents: project?.local_content?.map((lc) => decodeProjectLocalContent(lc)) || [],
        organisationDisplayName: resolveOrganisationDisplayName(project.organisation_name, project.canonical_name),
        organisationId: project.organisation_id,
        organisationLogo: presignedUrl,
        organisationName: project.organisation_name,
        policies: project.policies.map((p) => decodeProjectPolicy(p)),
        projectAccessLevel: project.project_access_level,
        projectAddress: decodeContactInfo(project.project_address),
        projectEndDate: project.project_end_date,
        projectFrameworks: (project.project_frameworks || []).map((framework) => decodeProjectFramework(framework)),
        projectName: project.project_name,
        projectStartDate: project.project_start_date,
        reportCadence: project.report_cadence,
        reportExists: project.report_exists,
        reportingDueDate: project.reporting_due_date,
        sector: project.sector,
        targetsEnvironmental: (project.targets_environmental || []).map((t) => decodeProjectEnvironmentalTarget(t)),
        userPackageId: project.user_package_id,
        workforceProfileFullTimeMonthlyHours: project.workforce_profile_full_time_monthly_hours ?? undefined
    };
};
export const encodeProject = (project) => {
    return {
        archived: project.archived,
        closed_date: project.closedDate,
        collaborators: project.collaborators || [],
        commitments_environmental: (project.commitmentsEnvironmental || []).map((c) => encodeProjectEnvironmentalCommitment(c)),
        commitments_hours: project.commitmentsHours.map((c) => encodeProjectLabourHourCommitment(c)),
        commitments_spend: project.commitmentsSpend.map((c) => encodeProjectSocialSpendCommitment(c)),
        created: new Date(project.created),
        emission_factors: (project.emissionFactors || []).map(encodeEmissionFactor),
        estimated_progress: project.estimatedProgress,
        financial: encodeFinancial(project.financial),
        financial_id: project.financial.id,
        is_closed: project.isClosed,
        is_shared: project.isShared,
        local_content: project?.localContents?.map((lc) => encodeProjectLocalContent(lc)) || [], //TODO: Remove this when local content is added to backend
        organisation_id: project.organisationId,
        organisation_logo: project.organisationLogo,
        organisation_name: project.organisationName,
        policies: project.policies.map((p) => encodeProjectPolicy(p)),
        project_access_level: project.projectAccessLevel,
        project_address: encodeContactInfo(project.projectAddress),
        project_contact_info_id: project.projectAddress.id,
        project_end_date: project.projectEndDate,
        project_frameworks: (project.projectFrameworks || []).map((framework) => encodeProjectFramework(framework)),
        project_id: project.id,
        project_name: project.projectName,
        project_start_date: project.projectStartDate,
        report_cadence: project.reportCadence,
        report_exists: project.reportExists,
        reporting_due_date: project.reportingDueDate,
        sector: project.sector,
        targets_environmental: (project.targetsEnvironmental || []).map((t) => encodeProjectEnvironmentalTarget(t)),
        user_package_id: project.userPackageId || "",
        workforce_profile_full_time_monthly_hours: project.workforceProfileFullTimeMonthlyHours
    };
};
export const decodeProjectDetail = async (project, getImageUrl) => {
    const presignedUrl = project.organisation_logo.length > 0
        ? await getImageUrl(project.organisation_logo)
        : "";
    return {
        archived: project.archived,
        closedDate: project.closed_date,
        collaboratorDetails: decodeCollaboratorDetails(project.collaborator_details, project.collaborators),
        collaborators: project.collaborators || [],
        contractorPackageId: project.contractor_package_id,
        created: new Date(project.created),
        estimatedProgress: project.estimated_progress,
        id: project.project_id,
        isAccepted: project.is_accepted,
        isClosed: project.is_closed ?? false,
        organisationDisplayName: resolveOrganisationDisplayName(project.organisation_name, project.canonical_name),
        organisationId: project.organisation_id,
        organisationLogo: presignedUrl,
        organisationName: project.organisation_name,
        packageDescription: project.package_description,
        packageType: project.package_type,
        projectAccessLevel: project.project_access_level,
        projectAddress: decodeContactInfo(project.project_address),
        projectContactInfoId: project.project_contact_info_id,
        projectEndDate: project.project_end_date,
        projectName: project.project_name,
        projectStartDate: project.project_start_date,
        reportingDueDate: project.reporting_due_date,
        sector: project.sector,
        workforceProfileFullTimeMonthlyHours: project.workforce_profile_full_time_monthly_hours ?? undefined
    };
};
export const encodeProjectDetails = (project) => {
    return {
        archived: project.archived,
        closed_date: project.closedDate,
        collaborator_details: (project.collaboratorDetails ?? []).map(encodeCollaboratorDetail),
        collaborators: project.collaborators || [],
        contractor_package_id: project.contractorPackageId,
        created: new Date(project.created),
        estimated_progress: project.estimatedProgress,
        is_accepted: project.isAccepted,
        is_closed: project.isClosed,
        organisation_id: project.organisationId,
        organisation_logo: project.organisationLogo,
        organisation_name: project.organisationName,
        package_type: project.packageType,
        project_access_level: project.projectAccessLevel,
        project_address: encodeContactInfo(project.projectAddress),
        project_contact_info_id: project.projectAddress.id,
        project_end_date: project.projectEndDate,
        project_id: project.id,
        project_name: project.projectName,
        project_start_date: project.projectStartDate,
        reporting_due_date: project.reportingDueDate,
        sector: project.sector,
        workforce_profile_full_time_monthly_hours: project.workforceProfileFullTimeMonthlyHours
    };
};
